import { useEffect, useState } from 'react';
import { fetchDevPersonas, startDevPersona, type DevPersona } from '../../lib/api/devPersonas';

// ─────────────────────────────────────────────────────────────────────
// DEV PERSONA BAR — «bytt til GA / senteradmin / klubbadmin / spiller»
// with one click. DEVELOPMENT ONLY, and the page does not decide that: it
// asks the API whether personas exist, and renders nothing at all when the
// route answers 404 — which is every deployment that has not opted in
// (apps/api/src/routes/devPersonas.ts).
//
// The sessions it obtains are REAL, minted by the same issuers login uses.
// What the tester then sees is the actual authorization answer.
// ─────────────────────────────────────────────────────────────────────

export interface DevPersonaBarProps {
  /** Which persona key is active, for highlighting. */
  current?: string | null;
  /**
   * Called with the persona's session once obtained. The caller decides what
   * to do with it — adopt it into the club session, or into the member one.
   */
  onSession: (
    persona: DevPersona,
    session: Awaited<ReturnType<typeof startDevPersona>>,
  ) => void | Promise<void>;
  /** Injectable for tests. */
  load?: typeof fetchDevPersonas;
  start?: typeof startDevPersona;
  /** Restrict to some persona kinds — /sa only wants club-session personas. */
  kinds?: readonly DevPersona['kind'][];
  /**
   * How it is dressed. Same switching logic either way.
   *
   *   'bar'   the floating pill at the bottom of the screen. Still how the
   *           signed-OUT screens offer it, because switching persona is how you
   *           get a session — it cannot live behind a menu that needs one.
   *   'list'  stacked rows, for inside the DEV menu. Signed in, a second black
   *           bar across the product is furniture nobody asked for; the menu is
   *           already open and the choices belong in it.
   */
  variant?: 'bar' | 'list';
}

export function DevPersonaBar({
  current = null,
  onSession,
  load = fetchDevPersonas,
  start = startDevPersona,
  kinds,
  variant = 'bar',
}: DevPersonaBarProps) {
  const [personas, setPersonas] = useState<DevPersona[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // The bar renders only where the API offers personas AND the caller is
    // signed in — the listing is behind the same session gate as the switch
    // itself, so a signed-out visitor sees nothing and learns nothing.
    void load().then((list) => {
      if (!cancelled) setPersonas(kinds ? list.filter((p) => kinds.includes(p.kind)) : list);
    });
    return () => {
      cancelled = true;
    };
  }, [load, kinds]);

  if (personas.length === 0) return null;

  // ONE handler, whichever dress. Duplicating it per variant is how the two
  // would start disagreeing about what a refusal means.
  const choose = async (p: DevPersona) => {
    setBusy(p.key);
    setNotice(null);
    try {
      const res = await start(p.key);
      if (res.state === 'ok') await onSession(p, res);
      else setNotice(res.state === 'refused' ? res.message : 'Kunne ikke bytte persona.');
    } finally {
      setBusy(null);
    }
  };

  const list = variant === 'list';

  return (
    <div
      className={
        list
          ? 'flex flex-col gap-0.5'
          : 'fixed bottom-3 left-1/2 -translate-x-1/2 z-50 rounded-full bg-black/80 text-white shadow-lg px-3 py-1.5 flex items-center gap-1.5 text-[11px]'
      }
      data-testid="dev-persona-bar"
      role="group"
      aria-label="Dev: bytt persona"
    >
      {!list && <span className="font-mono uppercase tracking-wider text-white/60 mr-1">Dev</span>}
      {personas.map((p) => (
        <button
          key={p.key}
          type="button"
          disabled={busy !== null}
          onClick={() => void choose(p)}
          className={
            list
              ? `w-full text-left rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                  current === p.key ? 'bg-emerald-700 text-white' : 'text-ink-2 hover:bg-bg-soft/70'
                } disabled:opacity-50`
              : `rounded-full px-2.5 py-1 ${
                  current === p.key ? 'bg-lime text-black font-semibold' : 'hover:bg-white/15'
                }`
          }
          aria-pressed={current === p.key}
          data-testid={`dev-persona-${p.key}`}
          /* A MEMBER PERSONA IS A ONE-WAY DOOR, and deliberately so: the persona
             router accepts a staff session and never a member one, so «a persona
             cannot bootstrap another persona from a member token». That makes this
             the one button the bar cannot bring you back from — said up front
             rather than discovered. */
          title={
            p.kind === 'member'
              ? 'Gir en medlemssesjon. Persona-baren forsvinner — logg inn som dev-kontoen igjen for å bytte tilbake.'
              : undefined
          }
        >
          {busy === p.key ? '…' : p.label}
          {p.kind === 'member' && (
            <span aria-hidden className={list ? 'ml-1 opacity-60' : 'ml-1 opacity-70'}>
              ↩︎
            </span>
          )}
        </button>
      ))}
      {notice && (
        <span
          className={list ? 'text-rose text-[11px] px-3 pt-1' : 'text-rose ml-1'}
          data-testid="dev-persona-notice"
        >
          {notice}
        </span>
      )}
    </div>
  );
}
